/**
 * Typed API Client for Pingdex Dashboard
 *
 * Provides type-safe methods for fetching dashboard data from Worker endpoints.
 * Uses Shopify App Bridge authenticatedFetch for secure API calls with automatic
 * session token handling.
 *
 * IMPORTANT: All authenticated API calls MUST use authenticatedFetch from App Bridge.
 * Never use raw fetch() for authenticated endpoints as it won't include session tokens.
 *
 * @see docs/PRD.md for API requirements
 * @see app/src/workers/api-routes.ts for backend implementation
 */

import type {
  DashboardStats,
  GoogleConnectionStatus,
  WebhookStatus,
  MerchantSettings,
  MerchantSettingsUpdate,
  BillingStatus,
  BillingUpgradeResponse,
  ApiErrorResponse,
  ApiResult,
  ApiRequestConfig,
  ApiErrorCode,
} from '../types/api';

// =============================================================================
// Constants
// =============================================================================

/** Default request timeout in milliseconds */
const DEFAULT_TIMEOUT_MS = 10000;

/**
 * API base URL
 *
 * Uses same-origin requests via Pages Functions proxy.
 * This solves Safari ITP blocking cross-origin requests in iframes.
 *
 * The Pages Function at /api/* proxies requests to the Worker.
 */
const API_BASE = '/api';

// =============================================================================
// Types
// =============================================================================

/**
 * Type for Shopify App Bridge authenticatedFetch function.
 * This is provided by the @shopify/app-bridge package.
 *
 * App Bridge v4+ provides this via the global `shopify` object that's
 * automatically initialized when the CDN script loads in Shopify Admin.
 */
type AuthenticatedFetch = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>;

// =============================================================================
// Internal Helpers
// =============================================================================

/**
 * Cached shop domain - stored on first successful retrieval.
 * This prevents losing the shop param when React Router navigates.
 */
let cachedShopDomain: string | null = null;

/** Session storage key for persisting shop domain across page refreshes */
const SHOP_DOMAIN_STORAGE_KEY = 'pingdex_shop_domain';

/**
 * Get the shop domain from URL parameters, cache, sessionStorage, or App Bridge.
 * The shop parameter is passed when redirecting from Shopify.
 *
 * Priority order:
 * 1. Module-level cache (fastest, survives React Router navigation)
 * 2. URL parameters (initial load from Shopify)
 * 3. sessionStorage (survives page refresh within session)
 * 4. App Bridge config (when embedded in Shopify Admin)
 */
function getShopDomain(): string | null {
  // Return cached value if available (survives React Router navigation)
  if (cachedShopDomain) {
    return cachedShopDomain;
  }

  // Try URL params first (initial load from Shopify)
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search);
    const shop = urlParams.get('shop');
    if (shop) {
      cachedShopDomain = shop;
      // Also persist to sessionStorage for page refresh resilience
      try {
        sessionStorage.setItem(SHOP_DOMAIN_STORAGE_KEY, shop);
      } catch {
        // Ignore storage errors (e.g., private browsing mode)
      }
      return shop;
    }

    // Try sessionStorage (survives page refresh)
    try {
      const storedShop = sessionStorage.getItem(SHOP_DOMAIN_STORAGE_KEY);
      if (storedShop) {
        cachedShopDomain = storedShop;
        return storedShop;
      }
    } catch {
      // Ignore storage errors
    }
  }

  // Finally try App Bridge config
  const shopify = (globalThis as unknown as { shopify?: { config?: { shop?: string } } }).shopify;
  const appBridgeShop = shopify?.config?.shop || null;
  if (appBridgeShop) {
    cachedShopDomain = appBridgeShop;
    try {
      sessionStorage.setItem(SHOP_DOMAIN_STORAGE_KEY, appBridgeShop);
    } catch {
      // Ignore storage errors
    }
  }

  if (!appBridgeShop) {
    console.error('[api-client] Could not determine shop domain from any source');
  }
  return appBridgeShop;
}

/**
 * Get a fetch function that works in both App Bridge and standalone contexts.
 *
 * - If App Bridge is available, use authenticatedFetch for session token auth
 * - Otherwise, fall back to regular fetch with shop parameter for API auth
 *
 * @returns A fetch function
 */
function getFetchFunction(): AuthenticatedFetch {
  // Try App Bridge first
  const shopify = (globalThis as unknown as { shopify?: { authenticatedFetch?: AuthenticatedFetch } }).shopify;

  if (shopify?.authenticatedFetch) {
    return shopify.authenticatedFetch;
  }

  // Fall back to regular fetch - we'll add shop param in makeRequest
  return fetch.bind(globalThis);
}

/**
 * Check if App Bridge is available
 */
function isAppBridgeAvailable(): boolean {
  const shopify = (globalThis as unknown as { shopify?: { authenticatedFetch?: AuthenticatedFetch } }).shopify;
  return !!shopify?.authenticatedFetch;
}

/**
 * Create a timeout promise that rejects after the specified duration.
 *
 * @param ms - Timeout duration in milliseconds
 * @param controller - AbortController to abort the fetch on timeout
 * @returns Promise that rejects with a timeout error
 */
function createTimeoutPromise(
  ms: number,
  controller: AbortController
): Promise<never> {
  return new Promise((_, reject) => {
    setTimeout(() => {
      controller.abort();
      reject(new Error(`Request timed out after ${ms}ms`));
    }, ms);
  });
}

/**
 * Parse an API error response from a non-2xx response.
 *
 * @param response - The fetch Response object
 * @returns Parsed ApiErrorResponse
 */
async function parseErrorResponse(response: Response): Promise<ApiErrorResponse> {
  try {
    const data: unknown = await response.json();
    // Type guard for error response shape
    if (
      data !== null &&
      typeof data === 'object' &&
      'error' in data &&
      'code' in data &&
      typeof (data as { error: unknown }).error === 'string' &&
      typeof (data as { code: unknown }).code === 'string'
    ) {
      return data as ApiErrorResponse;
    }
    // Fallback to generic error - safely access potential properties
    const errorObj = data as { error?: unknown; message?: unknown } | null;
    const errorMessage = typeof errorObj?.error === 'string'
      ? errorObj.error
      : typeof errorObj?.message === 'string'
        ? errorObj.message
        : 'Unknown error';
    return {
      error: errorMessage,
      code: mapStatusToErrorCode(response.status),
    };
  } catch {
    // JSON parsing failed
    return {
      error: `HTTP ${response.status}: ${response.statusText}`,
      code: mapStatusToErrorCode(response.status),
    };
  }
}

/**
 * Map HTTP status code to our error code enum.
 *
 * @param status - HTTP status code
 * @returns Corresponding ApiErrorCode
 */
function mapStatusToErrorCode(status: number): ApiErrorCode {
  switch (status) {
    case 401:
      return 'UNAUTHORIZED';
    case 404:
      return 'NOT_FOUND';
    case 400:
      return 'VALIDATION_ERROR';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'INTERNAL_ERROR';
    default:
      return 'UNKNOWN';
  }
}

/**
 * Make an authenticated API request with timeout and error handling.
 *
 * @param method - HTTP method
 * @param endpoint - API endpoint path (relative to API_BASE)
 * @param config - Request configuration
 * @param body - Optional request body for POST/PATCH requests
 * @returns Promise resolving to ApiResult
 */
async function makeRequest<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
  endpoint: string,
  config: ApiRequestConfig = {},
  body?: unknown
): Promise<ApiResult<T>> {
  const { timeout = DEFAULT_TIMEOUT_MS, signal: externalSignal } = config;

  // Create abort controller for timeout
  const controller = new AbortController();

  // If an external signal is provided, abort our controller when it aborts
  if (externalSignal) {
    externalSignal.addEventListener('abort', () => controller.abort());
  }

  try {
    const fetchFn = getFetchFunction();
    const appBridgeAvailable = isAppBridgeAvailable();

    // Build URL with shop parameter if not using App Bridge
    let url = `${API_BASE}${endpoint}`;
    if (!appBridgeAvailable) {
      const shop = getShopDomain();
      if (!shop) {
        return {
          success: false,
          error: {
            error: 'Shop domain not found. Please access this app through Shopify Admin.',
            code: 'UNAUTHORIZED',
          },
        };
      }
      // Add shop parameter to URL for authentication
      const urlObj = new URL(url, window.location.origin);
      urlObj.searchParams.set('shop', shop);
      url = urlObj.toString();
    }

    // Get id_token from URL if available (Shopify provides this for embedded apps)
    const idToken = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('id_token')
      : null;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Add Authorization header with id_token if available
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    const fetchOptions: RequestInit = {
      method,
      headers,
      signal: controller.signal,
    };

    if (body !== undefined) {
      fetchOptions.body = JSON.stringify(body);
    }

    // Race between fetch and timeout
    const response = await Promise.race([
      fetchFn(url, fetchOptions),
      createTimeoutPromise(timeout, controller),
    ]);

    // Handle non-2xx responses
    if (!response.ok) {
      const errorResponse = await parseErrorResponse(response);

      // Special handling for 401 - trigger re-authentication
      if (response.status === 401) {
        handleReauthentication();
      }

      return { success: false, error: errorResponse };
    }

    // Parse successful response
    const data = await response.json() as T;
    return { success: true, data };
  } catch (error) {
    // Handle different error types
    if (error instanceof Error) {
      // Check for abort/timeout
      if (error.name === 'AbortError' || error.message.includes('timed out')) {
        return {
          success: false,
          error: {
            error: 'Request timed out',
            code: 'TIMEOUT',
          },
        };
      }

      // Check for network errors
      if (error.message.includes('network') || error.message.includes('fetch')) {
        return {
          success: false,
          error: {
            error: 'Network error. Please check your connection.',
            code: 'NETWORK_ERROR',
          },
        };
      }

      // App Bridge not initialized
      if (error.message.includes('App Bridge not initialized')) {
        return {
          success: false,
          error: {
            error: error.message,
            code: 'UNAUTHORIZED',
          },
        };
      }

      // Unknown error
      return {
        success: false,
        error: {
          error: error.message,
          code: 'UNKNOWN',
        },
      };
    }

    // Fallback for non-Error throws
    return {
      success: false,
      error: {
        error: 'An unexpected error occurred',
        code: 'UNKNOWN',
      },
    };
  }
}

/**
 * Handle 401 unauthorized errors by triggering re-authentication.
 *
 * Uses App Bridge to redirect the user to Shopify's OAuth flow.
 * This handles cases where the session token has expired.
 */
function handleReauthentication(): void {
  // Access the global shopify object for redirect
  const shopify = (globalThis as unknown as {
    shopify?: {
      idToken?: () => Promise<string>;
    };
  }).shopify;

  if (shopify?.idToken) {
    // App Bridge v4+: Request a fresh ID token
    // This will trigger re-authentication if the session is invalid
    shopify.idToken().catch(() => {
      // Re-authentication failed silently
    });
  } else {
    // Fallback: reload the page to trigger OAuth flow
    // Use globalThis.location for broader compatibility (works in both browser and some runtimes)
    if (typeof globalThis !== 'undefined' && 'location' in globalThis) {
      (globalThis as unknown as { location: { reload: () => void } }).location.reload();
    }
  }
}

// =============================================================================
// Public API Functions
// =============================================================================

/**
 * Fetch dashboard statistics including today's pings and 30-day trends.
 *
 * @param config - Optional request configuration
 * @returns Promise resolving to dashboard stats or error
 *
 * @example
 * ```typescript
 * const result = await fetchDashboardStats();
 * if (result.success) {
 *   console.log('Today\'s pings:', result.data.today.totalPings);
 *   console.log('Trend data:', result.data.trend);
 * } else {
 *   console.error('Error:', result.error.message);
 * }
 * ```
 */
export async function fetchDashboardStats(
  config?: ApiRequestConfig
): Promise<ApiResult<DashboardStats>> {
  return makeRequest<DashboardStats>('GET', '/stats', config);
}

/**
 * Fetch Google OAuth connection status and quota usage.
 *
 * @param config - Optional request configuration
 * @returns Promise resolving to Google connection status or error
 *
 * @example
 * ```typescript
 * const result = await fetchGoogleStatus();
 * if (result.success) {
 *   if (result.data.connected) {
 *     console.log('Quota used:', result.data.quotaUsed, '/', result.data.quotaLimit);
 *   } else {
 *     console.log('Google not connected');
 *   }
 * }
 * ```
 */
export async function fetchGoogleStatus(
  config?: ApiRequestConfig
): Promise<ApiResult<GoogleConnectionStatus>> {
  return makeRequest<GoogleConnectionStatus>('GET', '/google/status', config);
}

/**
 * Fetch webhook subscription status.
 *
 * Returns status of all expected webhook subscriptions including which are
 * active, any failed deliveries, and timestamps of last received webhooks.
 *
 * @param config - Optional request configuration
 * @returns Promise resolving to webhook status or error
 *
 * @example
 * ```typescript
 * const result = await fetchWebhookStatus();
 * if (result.success) {
 *   console.log('Active webhooks:', result.data.activeCount, '/', result.data.totalCount);
 *   if (!result.data.allActive) {
 *     console.warn('Some webhooks are missing!');
 *   }
 * }
 * ```
 */
export async function fetchWebhookStatus(
  config?: ApiRequestConfig
): Promise<ApiResult<WebhookStatus>> {
  return makeRequest<WebhookStatus>('GET', '/webhooks/status', config);
}

/**
 * Re-subscribe to missing or failed webhook subscriptions.
 *
 * Attempts to recreate webhook subscriptions that are not currently active.
 *
 * @param config - Optional request configuration
 * @returns Promise resolving to updated webhook status or error
 *
 * @example
 * ```typescript
 * const result = await resubscribeWebhooks();
 * if (result.success) {
 *   console.log('Webhooks resubscribed. Active:', result.data.activeCount);
 * }
 * ```
 */
export async function resubscribeWebhooks(
  config?: ApiRequestConfig
): Promise<ApiResult<WebhookStatus>> {
  return makeRequest<WebhookStatus>('POST', '/webhooks/resubscribe', config);
}

/**
 * Fetch merchant settings including priorities and quota configuration.
 *
 * @param config - Optional request configuration
 * @returns Promise resolving to merchant settings or error
 *
 * @example
 * ```typescript
 * const result = await fetchSettings();
 * if (result.success) {
 *   console.log('Tier:', result.data.tier);
 *   console.log('New product priority:', result.data.priorityNewProducts);
 * }
 * ```
 */
export async function fetchSettings(
  config?: ApiRequestConfig
): Promise<ApiResult<MerchantSettings>> {
  return makeRequest<MerchantSettings>('GET', '/settings', config);
}

/**
 * Update merchant settings.
 *
 * Only include the settings you want to change. Unspecified settings
 * will remain unchanged.
 *
 * @param settings - Partial settings to update
 * @param config - Optional request configuration
 * @returns Promise resolving to void on success or error
 *
 * @example
 * ```typescript
 * // Update just the new product priority
 * const result = await updateSettings({ priorityNewProducts: 'high' });
 * if (result.success) {
 *   console.log('Settings updated');
 * }
 *
 * // Update multiple settings
 * await updateSettings({
 *   googleQuotaLimit: 500,
 *   staleInclusionDays: 7,
 *   priorityPriceChanges: 'ignore'
 * });
 * ```
 */
export async function updateSettings(
  settings: MerchantSettingsUpdate,
  config?: ApiRequestConfig
): Promise<ApiResult<void>> {
  // Return void data structure on success
  const result = await makeRequest<{ success: boolean }>('PATCH', '/settings', config, settings);

  if (result.success) {
    return { success: true, data: undefined };
  }
  return result;
}

/**
 * Disconnect Google account by deleting OAuth tokens.
 *
 * After disconnecting, the merchant will need to re-authorize to use
 * Google Indexing API features.
 *
 * @param config - Optional request configuration
 * @returns Promise resolving to void on success or error
 *
 * @example
 * ```typescript
 * const result = await disconnectGoogle();
 * if (result.success) {
 *   console.log('Google account disconnected');
 * } else {
 *   console.error('Failed to disconnect:', result.error.message);
 * }
 * ```
 */
export async function disconnectGoogle(
  config?: ApiRequestConfig
): Promise<ApiResult<void>> {
  const result = await makeRequest<{ success: boolean }>('POST', '/google/disconnect', config);

  if (result.success) {
    return { success: true, data: undefined };
  }
  return result;
}

// =============================================================================
// Billing API Functions
// =============================================================================

/**
 * Fetch current billing status including tier, usage, and subscription details.
 *
 * @param config - Optional request configuration
 * @returns Promise resolving to billing status or error
 *
 * @example
 * ```typescript
 * const result = await fetchBillingStatus();
 * if (result.success) {
 *   if (result.data.tier === 'pro') {
 *     console.log('Pro plan - unlimited pings');
 *   } else {
 *     console.log('Free plan:', result.data.dailyUsed, '/', result.data.dailyLimit, 'used');
 *   }
 * }
 * ```
 */
export async function fetchBillingStatus(
  config?: ApiRequestConfig
): Promise<ApiResult<BillingStatus>> {
  return makeRequest<BillingStatus>('GET', '/billing/status', config);
}

/**
 * Initiate upgrade to Pro tier via Shopify billing.
 *
 * Returns a confirmation URL that the merchant must be redirected to
 * for Shopify billing approval. After approval/decline, Shopify redirects
 * back to the app's billing callback.
 *
 * @param config - Optional request configuration
 * @returns Promise resolving to upgrade response with confirmation URL
 *
 * @example
 * ```typescript
 * const result = await initiateBillingUpgrade();
 * if (result.success) {
 *   // Redirect to Shopify billing page (must break out of iframe)
 *   window.open(result.data.confirmationUrl, '_top');
 * } else {
 *   console.error('Upgrade failed:', result.error.error);
 * }
 * ```
 */
export async function initiateBillingUpgrade(
  config?: ApiRequestConfig
): Promise<ApiResult<BillingUpgradeResponse>> {
  return makeRequest<BillingUpgradeResponse>('POST', '/billing/upgrade', config);
}

// =============================================================================
// Exported Helpers
// =============================================================================

/**
 * Get the current shop domain.
 * Exported for use by components that need the shop domain (e.g., for OAuth URLs).
 */
export { getShopDomain };

// =============================================================================
// Re-export types for convenience
// =============================================================================

export type {
  DashboardStats,
  GoogleConnectionStatus,
  WebhookStatus,
  WebhookSubscription,
  MerchantSettings,
  MerchantSettingsUpdate,
  BillingStatus,
  BillingUpgradeResponse,
  ApiErrorResponse,
  ApiResult,
  ApiRequestConfig,
  ApiErrorCode,
} from '../types/api';
